import * as http from '@/model/http'
import {
  arrayToObject,
  setHistory,
  setEntity,
  setEntities,
  getCachedEntity,
  getCachedEntities,
  getEntityByUrl,
  getEntitiesByUrl,
  getEntityByKey,
  getEntitiesByKeys,
  removeEntity,
  getAllEntitiesAsObject,
  expireHistory
} from '@/model/utils'
import store from '@/store'
import { buildUrlQuery } from '@/utils/url'
import { Image, Privacy } from '@/model/schema'
import {
  PreviewQuestion,
  Question,
  QuestionCounts,
  QuestionSearchSuggestion
} from './schema'
import { Tag } from '@/model/tag/schema'
import { FetchOptions } from '@/model/types'
import { MyQuestionReaction } from '../questionReaction/schema'
import { EntityType } from '@/store/data'
import { MyReaction } from '../reaction/schema'

export type FetchQuestionsParams = {
  keyword?: string | (string | null)[]
  skillId?: string
  sortBy?: string
  privacy?: Privacy
  tab: string
  page: number
}
const fetchQuestions = async (args: {
  params: FetchQuestionsParams
  options?: FetchOptions
}): Promise<Question[]> => {
  const url = `questions/search${buildUrlQuery(args.params)}`
  const cached = getCachedEntities<Question>('Question', url, args.options)
  if (cached) return cached
  const { data }: { data: Question[] } = await http.get(url)
  const questionIds = data.map((item) => {
    return `${item.id}`
  })
  // cache entity and history if needed
  if (!args.options?.disableCache) {
    setHistory(url, questionIds, args.options)
    data.forEach(normalizeCommentInQuestion)
    setEntities('Question', arrayToObject(data, 'id'))
  }
  return data
}

const normalizeCommentInQuestion = (question: Question) => {
  if (!question.collapseAnswers?.comments) return
  if (!question.collapseAnswers?.comments.length) return
  question.collapseAnswers.comments.forEach((comment) => {
    comment.questionId = question.id
    setEntity('Comment', comment, `${comment.id}`)

    for (const reply of comment.replies) {
      setEntity('CommentReply', reply, `${reply.id}`)
    }
  })
}

const getQuestionsByParams = (params: FetchQuestionsParams): Question[] => {
  const url = `questions/search${buildUrlQuery(params)}`
  return getEntitiesByUrl('Question', url)
}

const getQuestionsByIds = (ids: number[]): Question[] => {
  return getEntitiesByKeys(
    'Question',
    ids.map((id) => `${id}`)
  )
}

export type FetchQuestionParams = { id: number | string }
const fetchQuestion = async (args: {
  params: FetchQuestionParams
  options?: FetchOptions
}): Promise<Question> => {
  const url = `questions/${args.params.id}`
  const cached = getCachedEntity<Question>('Question', url, args.options)
  if (cached) return cached
  const { data } = await http.get(url)
  const question: Question = data.question
  const id = `${question.id}`
  // cache entity and history if needed
  if (!args.options?.disableCache) {
    setHistory(url, [id], args.options)
    normalizeCommentInQuestion(question)
    setEntity('Question', question, `${question.id}`)
  }
  return question
}

const getQuestionByParams = (
  params: FetchQuestionParams
): Question | undefined => {
  const url = `questions/${params.id}`
  return getEntityByUrl('Question', url)
}

const getQuestionById = (id: number | string): Question | undefined => {
  return getEntityByKey('Question', `{{${id}}}`)
}

const createQuestion = async (params: {
  id: number
  title: string
  content?: string
  privacy: number
  images?: Image[]
  displayName: string
  tags: Tag[]
  files?: File[]
  removeImageIds?: number[]
  userName: string
  parent_id: number | null
  status: number
  nickname: string
  tagIds: number[]
  skillId?: number
}): Promise<Question> => {
  const formData = new FormData()
  Object.keys(params)
    .filter((key) => params[key])
    .forEach((key) => {
      if (key === 'nickname') {
        formData.append('user_name', params[key])
      } else if (key === 'files') {
        const files = params[key] || []
        for (let i = 0; i < files.length; i++) {
          formData.append('files[]', files[i])
        }
      } else {
        formData.append(key, params[key])
      }
    })
  const { data } = await http.post('/questions', formData)
  // re-fetch question, for data.question is not compatible with type Question
  await fetchQuestion({
    params: {
      id: data.question.id
    },
    options: {
      ignoreCache: true
    }
  })
  return getQuestionByParams({ id: data.question.id })!
}

const updateQuestion = async (params: {
  id: number
  title: string
  content?: string
  privacy: number
  images?: Image[]
  displayName: string
  tags: Tag[]
  files?: File[]
  removeImageIds?: number[]
  userName: string
  parent_id: number | null
  status: number
  nickname: string
  tagIds: number[]
  skillId?: number
}): Promise<Question> => {
  const formData = new FormData()
  formData.append('_method', 'PUT')
  Object.keys(params)
    .filter((key) => params[key])
    .forEach((key) => {
      if (key === 'nickname') {
        formData.append('user_name', params['nickname'])
      } else if (key === 'files') {
        const files = params[key] || []
        for (let i = 0; i < files.length; i++) {
          formData.append('files[]', files[i])
        }
      } else {
        formData.append(key, params[key])
      }
    })
  const { data } = await http.post(`/questions/${params.id}`, formData)
  // re-fetch question, for data.question is not compatible with type Question
  await fetchQuestion({
    params: {
      id: data.question.id
    },
    options: {
      ignoreCache: true
    }
  })
  return getQuestionByParams({ id: data.question.id })!
}

const closeQuestion = async (params: { question: Question }) => {
  const { data } = await http.post(`/questions/${params.question.id}/close`)
  params.question.closedAt = data.question.closed_at
  setEntity('Question', params.question, `${params.question.id}`)
  return params.question
}

const deleteQuestion = async (params: {
  id: number | string
}): Promise<Question> => {
  const url = `/questions/${params.id}`
  const { data }: { data: Question } = await http.delete(url, params)
  removeEntity('Question', `${params.id}`)
  return data
}

export type FetchQuestionCountsParams = {
  privacy: Privacy
  keyword?: string
  skillId?: string
}
const fetchQuestionCounts = async (args: {
  params: FetchQuestionCountsParams
  options?: FetchOptions
}): Promise<QuestionCounts> => {
  const url = `questions/counts${buildUrlQuery(args.params)}`
  if (!args.options?.ignoreCache) {
    const cached = getCachedEntity<QuestionCounts>(
      'QuestionCounts',
      url,
      args.options
    )
    if (cached) return cached
  }

  const { data }: { data: QuestionCounts } = await http.get(url)
  // use url as key, because the response of this api does not have a key to identify itself
  const key = url
  // cache entity and history if needed
  if (!args.options?.disableCache) {
    setHistory(url, [key], args.options)
    setEntity('QuestionCounts', data, key)
  }
  return data
}

export type FetchAllQuestionsCountParams = {
  privacy: Privacy
}

const fetchAllQuestionsCount = async (args: {
  params: FetchAllQuestionsCountParams
  options?: FetchOptions
}): Promise<number> => {
  const url = `questions/counts${buildUrlQuery({
    ...args.params,
    all: 'true'
  })}`

  if (!args.options?.ignoreCache) {
    const cached = getCachedEntity<number>(
      'AllQuestionsCount',
      url,
      args.options
    )
    if (cached) return cached
  }

  const { data }: { data: number } = await http.get(url)
  const key = url

  // cache entity and history if needed
  if (!args.options?.disableCache) {
    setHistory(url, [key], args.options)
    setEntity('AllQuestionsCount', data, key)
  }
  return data
}

const updateLikedQuestionsCount = async (args: {
  params: FetchQuestionCountsParams
}): Promise<number> => {
  const url = `questions/counts${buildUrlQuery({
    ...args.params,
    onlyLiked: 'true'
  })}`

  const { data }: { data: number } = await http.get(url)

  const questionCountsUrl = `questions/counts${buildUrlQuery(args.params)}`
  const questionCountsEntities =
    getAllEntitiesAsObject<QuestionCounts>('QuestionCounts')
  for (const key of Object.keys(questionCountsEntities)) {
    if (key === questionCountsUrl) {
      const currentValue = { ...questionCountsEntities[key] }
      currentValue.liked = data
      setEntity('QuestionCounts', currentValue, key)
    } else {
      removeEntity('QuestionCounts', key)
    }
  }

  return data
}

const removeAllEntities = (type: EntityType) => {
  const entities = getAllEntitiesAsObject(type)
  for (const key of Object.keys(entities)) {
    removeEntity(type, key)
  }
}

const updateLikedQuestionsAfterUnlike = (unlikedQuestionId: number) => {
  const idStr = unlikedQuestionId.toString()
  const question = getAllEntitiesAsObject<Question>('Question')[idStr]
  if (!question) return

  // check if the user still liking the question or its answers
  // no need to remove question from liked list
  const myQuestionReactions =
    getAllEntitiesAsObject<MyQuestionReaction>('MyQuestionReaction')

  if (
    myQuestionReactions[unlikedQuestionId] &&
    myQuestionReactions[unlikedQuestionId]['like']
  ) {
    return
  }

  const myAnswerReactions = getAllEntitiesAsObject<MyReaction>('MyReaction')
  const answers = question.collapseAnswers?.comments || []
  for (const answer of answers) {
    if (myAnswerReactions[answer.id] && myAnswerReactions[answer.id]['like']) {
      return
    }
  }

  if (
    myQuestionReactions[unlikedQuestionId] &&
    myQuestionReactions[unlikedQuestionId]['like']
  ) {
    return
  }

  const histories = (store.state as any).data.history
  for (const key of Object.keys(histories)) {
    const historyData = histories[key].keys
    const targetIndex = historyData.indexOf(idStr)
    if (key.includes('tab=liked') && targetIndex !== -1) {
      const newData = historyData.filter((data: string) => data !== idStr)
      setHistory(key, newData)
    }
  }
}

// expire all liked questions in history to refresh
const refreshAllQuestionsByTab = (tabName: string) => {
  const histories = (store.state as any).data.history
  for (const key of Object.keys(histories)) {
    if (key.includes(`tab=${tabName}`)) {
      // expire history to refresh liked questions list
      expireHistory(key)
    }
  }
}

const getQuestionCountsByParams = (
  params: FetchQuestionCountsParams
): QuestionCounts | undefined => {
  const url = `questions/counts${buildUrlQuery(params)}`
  return getEntityByUrl('QuestionCounts', url)
}

export type FetchSearchSuggestionsParams = {
  keyword?: string
  skillId?: number
  limit: number
}
const fetchSearchSuggestions = async (args: {
  params: FetchSearchSuggestionsParams
  options?: FetchOptions
}): Promise<QuestionSearchSuggestion[]> => {
  const url = `questions/suggestion-search${buildUrlQuery(args.params)}`
  const cached = getCachedEntities<QuestionSearchSuggestion>(
    'QuestionSearchSuggestion',
    url,
    args.options
  )
  if (cached) return cached
  const { data }: { data: QuestionSearchSuggestion[] } = await http.get(url)
  const keys = data.map((item) => {
    return `${item.id}`
  })
  // cache entity and history if needed
  if (!args.options?.disableCache) {
    setHistory(url, keys, args.options)
    setEntities('QuestionSearchSuggestion', arrayToObject(data, 'id'))
  }
  return data
}

const getSearchSuggestionsByParams = (
  params: FetchSearchSuggestionsParams
): QuestionSearchSuggestion[] => {
  const url = `questions/suggestion-search${buildUrlQuery(params)}`
  return getEntitiesByUrl('QuestionSearchSuggestion', url)
}

const getSearchSuggestionsByIds = (
  ids: number[]
): QuestionSearchSuggestion[] => {
  return getEntitiesByKeys(
    'QuestionSearchSuggestion',
    ids.map((id) => `${id}`)
  )
}

export type FetchPreviewQuestionsParams = {
  ids: number[]
}
const fetchPreviewQuestions = async (args: {
  params: FetchPreviewQuestionsParams
  options?: FetchOptions
}): Promise<PreviewQuestion[]> => {
  const url = `/questions/previews?ids=${args.params.ids.join()}`
  const cached = getCachedEntities<PreviewQuestion>(
    'PreviewQuestion',
    url,
    args.options
  )
  if (cached) return cached
  const { data }: { data: PreviewQuestion[] } = await http.get(url)
  const questionIds = data.map((item) => {
    return `${item.id}`
  })
  // cache entity and history if needed
  if (!args.options?.disableCache) {
    setHistory(url, questionIds, args.options)
    setEntities('PreviewQuestion', arrayToObject(data, 'id'))
  }
  return data
}

const react = async (params: {
  questionId: number
  reactionType: string
}): Promise<void> => {
  const url = `/questions/${params.questionId}/reactions`
  const { data } = await http.post(url, {
    reactionType: params.reactionType
  })
  // update MyQuestionReaction
  const myReactions =
    getAllEntitiesAsObject<MyQuestionReaction>('MyQuestionReaction')
  const reaction: MyQuestionReaction = myReactions[params.questionId] || {
    questionId: params.questionId,
    like: null
  }
  reaction[params.reactionType] = data.record.id
  setEntity('MyQuestionReaction', reaction, `${params.questionId}`)

  // update question
  const question = getEntityByKey<Question>('Question', `${params.questionId}`)
  if (question) {
    const countName = params.reactionType === 'like' ? 'likeCount' : null
    if (countName) question[countName] += 1
  }
}

const unreact = async (params: {
  questionId: number
  reactionId: number
  reactionType: string
}): Promise<void> => {
  const url = `/questions/${params.questionId}/reactions/${params.reactionId}`
  await http.delete(url, {
    reactionType: params.reactionType
  })
  // update MyQuestionReaction
  const myReactions =
    getAllEntitiesAsObject<MyQuestionReaction>('MyQuestionReaction')
  const reaction = myReactions[params.questionId]
  reaction[params.reactionType] = null
  // update question
  const question = getEntityByKey<Question>('Question', `${params.questionId}`)
  if (question) {
    const countName = params.reactionType === 'like' ? 'likeCount' : null
    if (countName) question[countName] -= 1
  }
}

export default {
  fetchQuestions,
  getQuestionsByParams,
  getQuestionByParams,
  getQuestionsByIds,
  getQuestionById,
  fetchQuestion,
  createQuestion,
  updateQuestion,
  closeQuestion,
  deleteQuestion,
  fetchSearchSuggestions,
  getSearchSuggestionsByParams,
  getSearchSuggestionsByIds,
  fetchQuestionCounts,
  getQuestionCountsByParams,
  fetchPreviewQuestions,
  react,
  unreact,
  updateLikedQuestionsCount,
  removeAllEntities,
  updateLikedQuestionsAfterUnlike,
  refreshAllQuestionsByTab,
  fetchAllQuestionsCount
}
